import { Controller } from "@hotwired/stimulus"

// Manages the job profile selection mode for report options.
// Allows switching between handpicking option values and selecting them
// based on a job profile.
export default class extends Controller {
  static values = {
    candidateJobProfileId: Number,
    initialMode: String,
    optionValueNames: Object,
    profileOptionValues: Object,
    warning: String,
  }

  static targets = [
    "optionSection",
    "optionSelect",
    "jobProfileFields",
    "jobProfileName",
    "jobProfileSelect",
    "mode",
    "placeholder",
    "warning",
  ]

  connect() {
    requestAnimationFrame(() => {
      this.initializeForMode()
    })
  }

  initializeForMode() {
    const isJobProfile = this.initialModeValue === "job_profile"

    if (isJobProfile) {
      this.initializeWithJobProfile()
    } else {
      this.initializeWithHandpick()
    }
  }

  initializeWithJobProfile() {
    const profileId = this.candidateJobProfileIdValue
    if (profileId && this.hasOptionSelectTarget) {
      this.prefillOptionValues(profileId)
      this.showUnavailableWarning(profileId)
    } else {
      this.showPlaceholder()
    }
  }

  initializeWithHandpick() {
    this.jobProfileFieldsTarget.classList.add("hidden")
    this.showOptionSections()
    this.placeholderTarget.classList.add("hidden")
    this.hideWarning()
  }

  selectMode() {
    const mode = this.modeTargets.find((target) => target.checked).value
    const isJobProfile = mode === "job_profile"

    this.jobProfileFieldsTarget.classList.toggle("hidden", !isJobProfile)

    if (isJobProfile) {
      if (this.jobProfileSelectTarget.value) {
        this.selectJobProfile()
      } else {
        this.showPlaceholder()
      }
    } else {
      this.clearOptionValues()
      this.clearJobProfileName()
      this.showOptionSections()
      this.placeholderTarget.classList.add("hidden")
      this.hideWarning()
    }
  }

  selectJobProfile() {
    const profileId = this.jobProfileSelectTarget.value

    if (profileId) {
      this.setJobProfileName()
      this.prefillOptionValues(profileId)
      this.showUnavailableWarning(profileId)
    } else {
      this.clearJobProfileName()
      this.showPlaceholder()
      this.hideWarning()
    }
  }

  showPlaceholder() {
    this.hideOptionSections()
    this.placeholderTarget.classList.remove("hidden")
  }

  prefillOptionValues(profileId) {
    this.optionSelectTargets.forEach((selectTarget) => {
      const optionKey = selectTarget.dataset.reportOptionPrefillOptionKey
      const optionValues = this.profileOptionValuesValue[optionKey]?.[profileId] || []
      const availableKeys = new Set(Object.keys(selectTarget.tomselect.options))
      const available = optionValues.filter((key) => availableKeys.has(key))

      selectTarget.tomselect.clear()
      selectTarget.tomselect.setValue(available)
    })

    this.showOptionSections()
    this.placeholderTarget.classList.add("hidden")
  }

  setJobProfileName() {
    if (!this.hasJobProfileNameTarget) return

    this.jobProfileNameTarget.value =
      this.jobProfileSelectTarget.selectedOptions[0]?.text || ""
  }

  clearJobProfileName() {
    if (this.hasJobProfileNameTarget) this.jobProfileNameTarget.value = ""
  }

  showUnavailableWarning(profileId) {
    const unavailable = this.optionSelectTargets.flatMap((selectTarget) => {
      const optionKey = selectTarget.dataset.reportOptionPrefillOptionKey
      const optionValues = this.profileOptionValuesValue[optionKey]?.[profileId] || []
      const availableKeys = new Set(Object.keys(selectTarget.tomselect.options))

      return optionValues.filter((key) => !availableKeys.has(key))
    })

    if (unavailable.length === 0) {
      this.hideWarning()
      return
    }

    const names = unavailable
      .map((key) => this.optionValueNamesValue[key] || key)
      .join(", ")

    this.warningTarget.textContent = this.warningValue.replace(
      "%{names}",
      names,
    )
    this.warningTarget.classList.remove("hidden")
  }

  hideWarning() {
    this.warningTarget.classList.add("hidden")
  }

  clearOptionValues() {
    this.optionSelectTargets.forEach((selectTarget) => {
      selectTarget.tomselect.clear()
    })
  }

  showOptionSections() {
    this.optionSectionTargets.forEach((sectionTarget) => {
      sectionTarget.classList.remove("hidden")
    })
  }

  hideOptionSections() {
    this.optionSectionTargets.forEach((sectionTarget) => {
      sectionTarget.classList.add("hidden")
    })
  }
}
